import React from 'react';
import { Loader2, Plus, Pencil, Trash2, Check, X, Settings, Paperclip, Banknote, ShoppingBag } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { useConfirm } from '../ui/ConfirmProvider';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Field, Input, Select, UnitInput } from '../ui/Field';
import { StatusFilterChips } from '../ui/StatusFilterChips';

/**
 * Centralise les achats/factures fournisseurs de l'établissement (ce qu'il paie lui-même) — distinct
 * des Notes de frais (remboursement salarié) et de Commandes (ce qu'un client final doit à
 * l'établissement pour un événement). Ajouté le 2026-09-24.
 */

interface AchatCategory { id: string; label: string; }
interface Supplier { id: string; name: string; }
interface Achat {
    id: string;
    supplier_id: string | null;
    supplier_name_snapshot: string | null;
    label: string;
    category: string | null;
    amount_ht: number | null;
    amount_ttc: number;
    vat_rate: number | null;
    purchase_date: string;
    payment_status: 'a_payer' | 'paye';
    payment_date: string | null;
    payment_method: string | null;
    has_document: number;
    created_at: string;
}

const fmt = (n?: number | null) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);
const fmtDate = (d?: string | null) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; }
};
const todayInput = () => new Date().toISOString().slice(0, 10);

const fileToBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
});

const EMPTY_FORM = {
    id: '', label: '', category: '', supplier_id: '', supplier_name: '',
    amount_ht: '', amount_ttc: '', vat_rate: '', purchase_date: todayInput(),
};

export const AchatsModule: React.FC = () => {
    const confirmAction = useConfirm();
    const [achats, setAchats] = React.useState<Achat[]>([]);
    const [categories, setCategories] = React.useState<AchatCategory[]>([]);
    const [suppliers, setSuppliers] = React.useState<Supplier[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [filter, setFilter] = React.useState('all');
    const [busyId, setBusyId] = React.useState<string | null>(null);

    const [showForm, setShowForm] = React.useState(false);
    const [form, setForm] = React.useState(EMPTY_FORM);
    const [documentFile, setDocumentFile] = React.useState<File | null>(null);
    const [saving, setSaving] = React.useState(false);
    const [formError, setFormError] = React.useState('');

    const [showCategories, setShowCategories] = React.useState(false);
    const [newCategory, setNewCategory] = React.useState('');
    const [editingCategoryId, setEditingCategoryId] = React.useState<string | null>(null);
    const [editingCategoryLabel, setEditingCategoryLabel] = React.useState('');
    const [categoryBusy, setCategoryBusy] = React.useState(false);
    const [categoryError, setCategoryError] = React.useState('');

    const load = () => {
        Promise.all([
            moduleApi.getAchats().catch(() => []),
            moduleApi.getAchatsCategories().catch(() => []),
            moduleApi.getSuppliers().catch(() => []),
        ]).then(([a, c, s]: any[]) => {
            setAchats(Array.isArray(a) ? a : []);
            setCategories(Array.isArray(c) ? c : []);
            setSuppliers(Array.isArray(s) ? s : []);
        }).finally(() => setLoading(false));
    };
    React.useEffect(() => { load(); }, []);

    const loadCategories = () => {
        moduleApi.getAchatsCategories().then((d: any) => setCategories(Array.isArray(d) ? d : [])).catch(() => {});
    };

    const openCreate = () => {
        setForm({ ...EMPTY_FORM, category: categories[0]?.label || '', purchase_date: todayInput() });
        setDocumentFile(null);
        setFormError('');
        setShowForm(true);
    };

    const openEdit = (a: Achat) => {
        setForm({
            id: a.id, label: a.label, category: a.category || '',
            supplier_id: a.supplier_id || '', supplier_name: a.supplier_name_snapshot || '',
            amount_ht: a.amount_ht != null ? String(a.amount_ht) : '',
            amount_ttc: String(a.amount_ttc), vat_rate: a.vat_rate != null ? String(a.vat_rate) : '',
            purchase_date: a.purchase_date,
        });
        setDocumentFile(null);
        setFormError('');
        setShowForm(true);
    };

    const handleSave = async () => {
        const amountTtc = Number(form.amount_ttc);
        if (!form.label.trim()) { setFormError('Libellé requis.'); return; }
        if (!amountTtc || amountTtc <= 0) { setFormError('Montant requis.'); return; }
        if (!form.purchase_date) { setFormError('Date requise.'); return; }
        setSaving(true);
        setFormError('');
        try {
            const payload: any = {
                label: form.label.trim(),
                category: form.category || null,
                supplier_id: form.supplier_id || null,
                supplier_name: form.supplier_name.trim() || null,
                amount_ht: form.amount_ht ? Number(form.amount_ht) : null,
                amount_ttc: amountTtc,
                vat_rate: form.vat_rate ? Number(form.vat_rate) : null,
                purchase_date: form.purchase_date,
            };
            if (documentFile) {
                const data = await fileToBase64(documentFile);
                payload.document = { data, mime_type: documentFile.type || 'application/octet-stream', file_name: documentFile.name };
            }
            const res: any = form.id ? await moduleApi.updateAchat(form.id, payload) : await moduleApi.createAchat(payload);
            if (res?.error) { setFormError(res.error); return; }
            setShowForm(false);
            load();
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!(await confirmAction({ title: 'Supprimer cet achat ?', description: 'Cette action est définitive.', confirmLabel: 'Supprimer', destructive: true }))) return;
        setBusyId(id);
        try {
            await moduleApi.deleteAchat(id);
            load();
        } finally {
            setBusyId(null);
        }
    };

    const toggleStatus = async (a: Achat) => {
        setBusyId(a.id);
        try {
            await moduleApi.updateAchatStatus(a.id, a.payment_status === 'paye' ? 'a_payer' : 'paye');
            load();
        } finally {
            setBusyId(null);
        }
    };

    // window.open() doit s'exécuter de façon SYNCHRONE au clic (avant tout await), sous peine d'être
    // bloqué silencieusement par le navigateur — même piège que sur les notes de frais.
    const viewDocument = async (achatId: string, win: Window | null) => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`/api/achats/${achatId}/document`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) { win?.close(); return; }
            const blob = await res.blob();
            if (win) win.location.href = URL.createObjectURL(blob);
        } catch { win?.close(); }
    };

    const handleAddCategory = async () => {
        const label = newCategory.trim();
        if (!label) return;
        setCategoryBusy(true);
        setCategoryError('');
        try {
            const res: any = await moduleApi.createAchatsCategory(label);
            if (res?.error) { setCategoryError(res.error); return; }
            setNewCategory('');
            loadCategories();
        } finally { setCategoryBusy(false); }
    };

    const handleRenameCategory = async (id: string) => {
        const label = editingCategoryLabel.trim();
        if (!label) return;
        setCategoryBusy(true);
        setCategoryError('');
        try {
            const res: any = await moduleApi.updateAchatsCategory(id, label);
            if (res?.error) { setCategoryError(res.error); return; }
            setEditingCategoryId(null);
            loadCategories();
        } finally { setCategoryBusy(false); }
    };

    const handleDeleteCategory = async (id: string) => {
        setCategoryBusy(true);
        setCategoryError('');
        try {
            const res: any = await moduleApi.deleteAchatsCategory(id);
            if (res?.error) { setCategoryError(res.error); return; }
            loadCategories();
        } finally { setCategoryBusy(false); }
    };

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;

    const filtered = filter === 'all' ? achats : achats.filter((a) => a.payment_status === filter);
    const totalAPayer = achats.filter((a) => a.payment_status === 'a_payer').reduce((s, a) => s + Number(a.amount_ttc || 0), 0);

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-xl font-bold text-content flex items-center gap-2"><ShoppingBag size={20} /> Achats</h1>
                    <p className="text-xs text-content-muted mt-1">
                        {totalAPayer > 0 ? `${fmt(totalAPayer)} restant à payer` : 'Tout est réglé.'}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="secondary" size="sm" onClick={() => { setShowCategories(true); setCategoryError(''); }}><Settings size={13} /> Catégories</Button>
                    <Button size="sm" onClick={openCreate}><Plus size={13} /> Ajouter un achat</Button>
                </div>
            </div>

            <StatusFilterChips
                value={filter}
                onChange={setFilter}
                options={[
                    { value: 'a_payer', label: 'À payer', count: achats.filter((a) => a.payment_status === 'a_payer').length },
                    { value: 'paye', label: 'Payé', count: achats.filter((a) => a.payment_status === 'paye').length },
                ]}
            />

            {filtered.length === 0 ? (
                <p className="text-xs text-content-muted">Aucun achat pour l'instant.</p>
            ) : (
                <div className="space-y-2">
                    {filtered.map((a) => (
                        <div key={a.id} className="p-4 rounded-2xl bg-surface-soft border border-border flex items-start justify-between gap-3 flex-wrap">
                            <div className="min-w-0 cursor-pointer" onClick={() => openEdit(a)}>
                                <p className="font-bold text-content text-sm">
                                    {fmt(a.amount_ttc)} <span className="text-content-muted font-normal">— {a.label}</span>
                                </p>
                                <p className="text-[11px] text-content-muted mt-0.5">
                                    {fmtDate(a.purchase_date)}
                                    {a.category && ` · ${a.category}`}
                                    {a.supplier_name_snapshot && ` · ${a.supplier_name_snapshot}`}
                                </p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                {!!a.has_document && (
                                    <button onClick={() => viewDocument(a.id, window.open('', '_blank'))}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border text-content-muted hover:text-content text-[11px] font-bold transition-all">
                                        <Paperclip size={11} /> Justificatif
                                    </button>
                                )}
                                <button onClick={() => toggleStatus(a)} disabled={busyId === a.id}
                                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-bold disabled:opacity-50 transition-all ${
                                        a.payment_status === 'paye'
                                            ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20'
                                            : 'bg-amber-500/10 text-amber-500 border border-amber-500/20 hover:bg-amber-500/20'
                                    }`}>
                                    <Banknote size={11} /> {a.payment_status === 'paye' ? 'Payé' : 'À payer'}
                                </button>
                                <button onClick={() => handleDelete(a.id)} disabled={busyId === a.id} className="p-1.5 text-content-muted hover:text-red-500 rounded-lg disabled:opacity-50"><Trash2 size={14} /></button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Modal open={showForm} onClose={() => setShowForm(false)} title={form.id ? "Modifier l'achat" : 'Ajouter un achat'} icon={<ShoppingBag size={16} className="text-brand-500" />} size="md">
                <div className="space-y-3">
                    {formError && <p className="text-xs text-red-500 font-semibold">{formError}</p>}
                    <Field label="Libellé" required>
                        {({ id }) => <Input id={id} value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} placeholder="Ex. Fournitures de bureau" />}
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Montant TTC" required>
                            {({ id }) => <UnitInput id={id} unit="€" type="number" step="0.01" min="0" value={form.amount_ttc} onChange={(e) => setForm((f) => ({ ...f, amount_ttc: e.target.value }))} />}
                        </Field>
                        <Field label="Montant HT" help="Facultatif">
                            {({ id }) => <UnitInput id={id} unit="€" type="number" step="0.01" min="0" value={form.amount_ht} onChange={(e) => setForm((f) => ({ ...f, amount_ht: e.target.value }))} />}
                        </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Date d'achat" required>
                            {({ id }) => <Input id={id} type="date" value={form.purchase_date} onChange={(e) => setForm((f) => ({ ...f, purchase_date: e.target.value }))} />}
                        </Field>
                        <Field label="Catégorie">
                            {({ id }) => (
                                <Select id={id} value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
                                    <option value="">—</option>
                                    {categories.map((cat) => <option key={cat.id} value={cat.label}>{cat.label}</option>)}
                                </Select>
                            )}
                        </Field>
                    </div>
                    <Field label="Fournisseur" help="Facultatif — sélectionnez un fournisseur déjà enregistré.">
                        {({ id }) => (
                            <Select id={id} value={form.supplier_id} onChange={(e) => {
                                const supplier = suppliers.find((s) => s.id === e.target.value);
                                setForm((f) => ({ ...f, supplier_id: e.target.value, supplier_name: supplier?.name || f.supplier_name }));
                            }}>
                                <option value="">—</option>
                                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </Select>
                        )}
                    </Field>
                    <Field label="Justificatif" help="Photo ou PDF de la facture (facultatif).">
                        {() => (
                            <label className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-dashed border-border text-xs text-content-muted cursor-pointer hover:border-brand-500/40 transition-colors">
                                <Paperclip size={14} /> {documentFile ? documentFile.name : 'Choisir un fichier'}
                                <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => setDocumentFile(e.target.files?.[0] || null)} />
                            </label>
                        )}
                    </Field>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                    <Button variant="secondary" onClick={() => setShowForm(false)}>Annuler</Button>
                    <Button onClick={handleSave} disabled={saving}>{saving ? 'Enregistrement...' : 'Enregistrer'}</Button>
                </div>
            </Modal>

            <Modal open={showCategories} onClose={() => setShowCategories(false)} title="Catégories d'achats" icon={<Settings size={16} className="text-brand-500" />} size="sm">
                <div className="space-y-4">
                    {categoryError && <p className="text-xs text-red-500 font-semibold">{categoryError}</p>}
                    <div className="space-y-1.5">
                        {categories.map((cat) => (
                            <div key={cat.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-surface-soft border border-border">
                                {editingCategoryId === cat.id ? (
                                    <>
                                        <Input value={editingCategoryLabel} onChange={(e) => setEditingCategoryLabel(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleRenameCategory(cat.id)}
                                            className="h-7 text-xs flex-1" autoFocus />
                                        <button onClick={() => handleRenameCategory(cat.id)} disabled={categoryBusy} className="p-1 text-emerald-500 hover:bg-emerald-500/10 rounded disabled:opacity-50"><Check size={14} /></button>
                                        <button onClick={() => setEditingCategoryId(null)} className="p-1 text-content-muted hover:text-content rounded"><X size={14} /></button>
                                    </>
                                ) : (
                                    <>
                                        <span className="flex-1 text-xs text-content font-semibold truncate">{cat.label}</span>
                                        <button onClick={() => { setEditingCategoryId(cat.id); setEditingCategoryLabel(cat.label); }} className="p-1 text-content-muted hover:text-content rounded"><Pencil size={13} /></button>
                                        <button onClick={() => handleDeleteCategory(cat.id)} disabled={categoryBusy} className="p-1 text-content-muted hover:text-red-500 rounded disabled:opacity-50"><Trash2 size={13} /></button>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                    <div className="flex items-center gap-2 pt-2 border-t border-border">
                        <Input value={newCategory} onChange={(e) => setNewCategory(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                            placeholder="Nouvelle catégorie" className="h-8 text-xs flex-1" />
                        <Button size="sm" onClick={handleAddCategory} disabled={categoryBusy || !newCategory.trim()}><Plus size={13} /> Ajouter</Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};
